import { SelectOptionType, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import {
  financialPortalPaymentHistoryByStudent,
  financialPortalPaymentHistoryByStudentDownload,
} from '../../../../api/requests/financialPortal';
import { downloadFromBlob } from '../../../../utils/blob';
import { PaymentHistoryTable } from '../../../FinancialPortal/PaymentHistory/parts/PaymentHistoryTable';
import {
  PaymentHistoryFilters,
  PaymentHistoryRow,
} from '../../../FinancialPortal/PaymentHistory/types';
import { PaymentHistoryDetailModal } from '../../../FinancialPortal/shared/PaymentHistoryDetailModal';
import { useStudentCurrentAccountLoader } from '../components/StudentCurrentAccountLoader';

import { SearchControls } from './parts/SearchControls';

const dictPrefix = 'financialPortal.currentAccount.tabs.paymentHistoryReceived';
const ITEMS_PER_PAGE = 10;

export default function PaymentHistory() {
  const { t } = useTranslation();
  const { data: studentData } = useStudentCurrentAccountLoader();
  const studentId = studentData!.studentId;

  const [tableData, setTableData] = useState<PaymentHistoryRow[]>([]);
  const [filters, setFilters] = useState<PaymentHistoryFilters>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);

  const paymentTypeOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: 'CR', label: t(`${dictPrefix}.paymentTypes.credit`) },
      { value: 'CO', label: t(`${dictPrefix}.paymentTypes.cash`) },
    ],
    [t],
  );

  const handleSearch = useCallback(
    async ({
      searchParams,
      page,
    }: {
      searchParams: PaymentHistoryFilters;
      page: number;
    }) => {
      setIsLoading(true);
      const { data, error } = await financialPortalPaymentHistoryByStudent(
        studentId,
        {
          ...searchParams,
          items: ITEMS_PER_PAGE,
          page,
        },
      );
      if (error) {
        addToast({
          text: t(`${dictPrefix}.errorOnSearch`),
          color: 'danger',
          icon: 'error',
        });
      } else {
        setTableData(data?.data ?? []);
        setTotalPages(data?.total_pages || 0);
        setTotalItems(data?.total || 0);
      }
      setIsLoading(false);
    },
    [studentId, t],
  );

  const handleDownload = useCallback(async () => {
    setIsDownloadLoading(true);
    const {
      data,
      error,
      status,
    } = await financialPortalPaymentHistoryByStudentDownload(
      studentId,
      filters,
    );
    if (status === 204) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.empty`),
        color: 'warning',
        icon: 'warning',
      });
      setIsDownloadLoading(false);
      return;
    }
    if (error) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.errorOnDownload`),
        color: 'danger',
        icon: 'error',
      });
      setIsDownloadLoading(false);
      return;
    }
    if (status === 200 && data) {
      addToast({
        text: t(`${dictPrefix}.generatingFile.download`),
        color: 'success',
        icon: 'success',
      });
      const blob = new Blob([data], { type: 'vnd.ms-excel' });
      downloadFromBlob(blob, `historial_pagos_${studentId}.xls`);
    }
    setIsDownloadLoading(false);
  }, [filters, studentId, t]);

  const handleClear = useCallback(() => {
    setFilters({});
    setCurrentPage(0);
  }, []);

  const [detailRowId, setDetailRowId] = useState<string | null>(null);

  const handleClickDetail = useCallback((row: PaymentHistoryRow) => {
    setDetailRowId(row.id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailRowId(null);
  }, []);

  const handleDownloadAttachment = useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  useEffect(() => {
    handleSearch({ searchParams: filters, page: currentPage });
  }, [handleSearch, currentPage, filters]);

  const hasFilters = useMemo(
    () =>
      Boolean(
        filters.paymentDate || filters.voucherNumber || filters.paymentType,
      ),
    [filters],
  );

  return (
    <Card className="p-3">
      <Row>
        <Col xs={12}>
          <SearchControls
            paymentTypeOptions={paymentTypeOptions}
            isDownloadLoading={isDownloadLoading}
            onSearch={(f) => {
              setCurrentPage(0);
              setFilters(f);
            }}
            onClear={handleClear}
            onDownload={handleDownload}
          />
        </Col>
        <Col xs={12}>
          <PaymentHistoryTable
            data={tableData}
            isLoadingResults={isLoading}
            totalItems={totalItems}
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            hasFilters={hasFilters}
            includeStudent={false}
            onChangePage={setCurrentPage}
            onClickDetail={handleClickDetail}
            onDownloadAttachment={handleDownloadAttachment}
          />
        </Col>
      </Row>
      <PaymentHistoryDetailModal
        paymentRowId={detailRowId}
        isOpen={detailRowId !== null}
        onClose={handleCloseDetail}
        includeStudent={false}
      />
    </Card>
  );
}
